import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { CURRENCY_DISPLAY_ORDER } from "@/helpers/currencyPresentation"
import { IRarity } from "@/store/apis/catalogsAPI/catalogs.types"
import { IInventoryItem } from "@/store/apis/inventoryAPI/inventory.types"
import {
  buildCurrencyRank,
  buildRarityRank,
  buildSearchCore,
  ownedCodesKey
} from "./core"
import { ISortSearchContext } from "./types"

const CURRENCY_RANK = buildCurrencyRank(CURRENCY_DISPLAY_ORDER)

export const useInventorySortSearch = (
  items: IInventoryItem[],
  rarities: IRarity[]
): ISortSearchContext => {
  const { t, i18n, ready } = useTranslation("inventory")
  const language = i18n.resolvedLanguage ?? i18n.language

  // Stable key: mutations refetch and replace the items array reference while
  // the code SET usually stays the same — the key keeps the expensive core memo
  // from rebuilding then. Memoized itself so keystroke renders pay O(1).
  const codesKey = useMemo(() => ownedCodesKey(items), [items])

  const exists = useCallback(
    (key: string) => i18n.exists(key, { ns: "inventory" }),
    [i18n]
  )

  // Deliberately narrow deps — NOT t/exists: the only events that change their
  // output (language change, lazy-namespace load) are already captured by
  // `language` and `ready`. Adding `t` would reintroduce exactly the churn the
  // stable key exists to prevent (spec §4.3).
  const core = useMemo(
    () =>
      buildSearchCore(codesKey === "" ? [] : codesKey.split("|"), {
        t,
        exists,
        language,
        ready
      }),
    [codesKey, language, ready]
  )

  const rarityRank = useMemo(() => buildRarityRank(rarities), [rarities])

  // The third, explicit memo (spec §4.3): an inline
  // `{ ...core, rarityRank, currencyRank }` in a render body would defeat the
  // step-2 memo every render.
  return useMemo(
    () => ({ ...core, rarityRank, currencyRank: CURRENCY_RANK }),
    [core, rarityRank]
  )
}
